import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminResults = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem("userEmail") || "";
  const role = localStorage.getItem("userRole");

  const [sections, setSections] = useState([]);
  const [adminResults, setAdminResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);
  const [sectionFilter, setSectionFilter] = useState("");

  const groupedByStudent = adminResults.reduce((acc, curr) => {
    const emailKey = curr.email;
    if (!acc[emailKey]) {
      acc[emailKey] = {
        email: emailKey,
        latest_completed_at: curr.completed_at,
        attempts: 1,
        latest_section: curr.section_name,
        latest_percentage: curr.percentage || 0,
      };
    } else {
      acc[emailKey].attempts += 1;
      if (new Date(curr.completed_at) > new Date(acc[emailKey].latest_completed_at)) {
        acc[emailKey].latest_completed_at = curr.completed_at;
        acc[emailKey].latest_section = curr.section_name;
        acc[emailKey].latest_percentage = curr.percentage || 0;
      }
    }
    return acc;
  }, {});

  const studentRows = Object.values(groupedByStudent);

  useEffect(() => {
    if (!email) {
      navigate("/register");
      return;
    }
    if (role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchSections();
    fetchResults();
  }, [email, role]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sections`, {
        params: { admin_email: email },
      });
      setSections(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (sectionId) => {
    setLoadingResults(true);
    if (!email) {
      toast.error("Missing admin email. Please log in again.");
      setLoadingResults(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/results`, {
        params: { admin_email: email, sectionId: sectionId || undefined },
        headers: { "x-admin-email": email },
      });
      setAdminResults(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load results");
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="dashboard-page">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div>
              <h1>Assessee Reports</h1>
              <p>Review assessee submissions and open detailed reports.</p>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn-secondary"
                  onClick={() => navigate("/dashboard")}>
                  Sections
                </button>
                <button
                  className="btn-secondary active-tab"
                >
                  Assessee Reports
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate("/admin-students")}
                >
                  Assessees
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate("/admin-config")}
                >
                  Configuration
                </button>
              </div>
            </div>
          </div>

          <div className="section-list">

            {loading || loadingResults ? (
              <div className="auth-card"><p>Loading assessee reports...</p></div>
            ) : studentRows.length === 0 ? (
              <div className="empty-state">
                <h3>No results yet</h3>
                <p>Results will appear after assessees submit assessments.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Latest Section</th>
                      <th>Latest Score</th>
                      <th>Completed</th>
                      <th>Attempts</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentRows.map((r) => (
                      <tr key={r.email}>
                        <td>{r.email}</td>
                        <td>{r.latest_section || "Section"}</td>
                        <td>{r.latest_percentage || 0}%</td>
                        <td>{new Date(r.latest_completed_at).toLocaleString()}</td>
                        <td>{r.attempts}</td>
                        <td>
                          <button
                            className="btn-secondary"
                            onClick={() => navigate(`/admin-student-report?email=${encodeURIComponent(r.email)}`)}
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                          >
                            <Eye size={14} />
                            View Report
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminResults;
